import { Check, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '../cn'
import { IconButton } from '../primitives/button'

export interface CopyFieldProps {
  readonly value: string
  readonly label: string
  readonly className?: string
  readonly compact?: boolean
  readonly mono?: boolean
}

/** Read-only value with a copy button: invite links, DNS records, request ids. */
export function CopyField({
  value,
  label,
  className,
  compact = false,
  mono = true,
}: CopyFieldProps) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => {
      setCopied(false)
    }, 1800)
    return () => {
      clearTimeout(timer)
    }
  }, [copied])

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-sm border border-line bg-paper',
        compact ? 'py-0.5 pr-0.5 pl-2' : 'py-1 pr-1 pl-3',
        className,
      )}
    >
      {!compact && (
        <span className="text-[11px] font-bold tracking-[0.05em] text-muted-2 uppercase">
          {label}
        </span>
      )}
      <span
        className={cn('min-w-0 truncate text-[12.5px] text-ink', mono && 'font-mono')}
        title={value}
      >
        {value}
      </span>
      <IconButton
        label={copied ? 'Copied' : `Copy ${label}`}
        variant="ghost"
        size="sm"
        onClick={() => void copy()}
      >
        {copied ? <Check className="text-recovered" /> : <Copy />}
      </IconButton>
      <span aria-live="polite" className="sr-only">
        {copied ? `${label} copied` : ''}
      </span>
    </div>
  )
}
